/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { FlatButton } from "src/framework/components/buttons/FlatButton";

import {
  Box,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import theme from "src/theme/light";
import { Formater, TypeRecords } from "src/framework/commons/Formater";
import { Projeto, SimulacaoFinanceira, SimulacaoParcelaItem } from "src/components/projetos/types";
import { FinanciamentoProvider } from "src/providers/financiamento";
import { SimulacaoFinanciamentoInput } from "src/providers/financiamento/types";
import { InputDateMui } from "src/framework/components/forms/inputs/InputDateMui";
import { format, isValid } from "date-fns";
import { cpfCnpj } from "src/framework/commons/masks";
import { ValidateUtil } from "src/framework/commons/ValidateUtil";
import InfoModal from "./InfoModal";
import { FINANCEIRA_PARCEIRO_ID, FINANCEIRA_SANTANDER_ID } from "../../constants";
import { notifierStore } from "src/framework/store/NotifierStore";
import { InputCPFCNPJ } from "src/framework/components/forms/inputs/InputCPFCNPJ";
import { InputDecimal3 } from "src/framework/components/forms/inputs/InputDecimal3";
import { InputComboBoxList } from "src/framework/components/forms/inputs/InputComboBoxList";

const regex = /nao existe valor pre aprovado/i;

interface Option {
  text: string;
  value: string;
}

interface DataProps {
  cpfCnpj: string;
  dataNascimentoOuFundacao: string;
  rendaOuFaturamentoMensal: number;
  financeira: Option | null;
  valorFinanciado: number;
}

interface ModalSimulacaoFinanciamentoProps {
  open: boolean;
  onClose: () => void;
  formState: Projeto
  onChangeFormState: (newState: Partial<Projeto>) => void
  valorTotal: number;
}

const useStyles = makeStyles({
  tableRow: {
    borderWidth: 1,
    borderStyle: 'solid none solid none',
    borderColor: 'rgba(0,0,0,0.2)'
  },
  list: {
    paddingInlineStart: 20,
  },
  installmentItem: {
    color: theme.palette.primary.main,
  }
})

const financiamentoProvider = new FinanciamentoProvider()

const ModalSimulacaoFinanciamento = ({ open, onClose, formState, onChangeFormState, valorTotal }: ModalSimulacaoFinanciamentoProps) => {
  const classes = useStyles()

  const [errorsInSimulation, setErrorsInSimulation] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<SimulacaoFinanceira[]>(formState.simulacao_financiamento?.simulacoes || [])
  const [financeirasOpts, setFinanceirasOpts] = useState<Option[]>([])
  
  const [data, setData] = useState<DataProps>({
    cpfCnpj: formState.cpf_cnpj_proposta || '',
    dataNascimentoOuFundacao: '',
    rendaOuFaturamentoMensal: 0,
    financeira: null,
    valorFinanciado: valorTotal || 0,
  })

  const isCpf = useMemo(()=>{
    const valor = (data?.cpfCnpj || '').replace(/[^a-zA-Z0-9]/g,'');
    return valor.length === 11;
  },[data.cpfCnpj])

  const cpfOrCnpjIsValid = useMemo(()=>{
    return isCpf ? ValidateUtil.isValidCPF(data.cpfCnpj) : ValidateUtil.isValidCNPJ(data.cpfCnpj)
  },[data, isCpf])

  useEffect(()=>{
    financiamentoProvider.listarFinanceiras().then((data)=>{
      const items = data?.data.data || []
      setFinanceirasOpts(items.filter(item => {
        const hideFinanceirasIds = [FINANCEIRA_SANTANDER_ID, FINANCEIRA_PARCEIRO_ID]
        return !hideFinanceirasIds.includes(item.id)
      }).map((item) => ({
        text: item.nome,
        value: item.codigo_integrador
      })))
    })
  },[])

  useEffect(()=>{
    if (formState.simulacao_financiamento && financeirasOpts.length) {
      setData({
        cpfCnpj: cpfCnpj({ target: { value: formState.simulacao_financiamento?.cpfCnpj } })?.target?.value || '',
        dataNascimentoOuFundacao: formState.simulacao_financiamento?.dataNascimentoOuFundacao || '',
        rendaOuFaturamentoMensal: formState.simulacao_financiamento?.rendaOuFaturamentoMensal || 0,
        financeira: financeirasOpts.find(financeira => financeira.value === formState.simulacao_financiamento?.financeira) || null,
        valorFinanciado: formState.simulacao_financiamento?.valorFinanciado || 0,
      })
    }
  },[formState.simulacao_financiamento, financeirasOpts])

  const simular = useCallback((partialData: Partial<DataProps> = {})=>{
    const localData = {
      ...data,
      ...partialData
    }

    const tipo = isCpf ? 'fisica' : 'juridica'
    const doc = localData.cpfCnpj?.replace(/[^a-zA-Z0-9]/g,'') || ''
    const phone = formState.telefone_proposta?.replace(/[^\d]+/g,'') || ''
    const dateIsValid = isValid(new Date(localData.dataNascimentoOuFundacao))

    if (!financeirasOpts.length || !dateIsValid || !localData.dataNascimentoOuFundacao || !localData.financeira || !localData.rendaOuFaturamentoMensal ||  !localData.valorFinanciado) return

    const payload: SimulacaoFinanciamentoInput = {
      pessoa: {
          nome: formState.cliente_proposta,
          tipo: tipo,
          documento: doc,
          telefone: phone,
          email: formState.email_proposta,
          endereco: {
              cep: formState.cep || '',
              logradouro: formState.endereco_proposta || '',
              numero: formState.numero || '',
              cidade: formState.cidade?.nome || '',
              bairro: formState.bairro || '',
              complemento: '',
              estado: formState.estado?.id || ''
          }
      },
      simulacao: {
          financeiras: financeirasOpts.filter(f => f?.value === data?.financeira?.value).map(financeira => financeira.value),
          data_nascimento_fundacao: format(new Date(localData.dataNascimentoOuFundacao), "yyyy-MM-dd"),
          renda_faturamento_mensal: Number(localData.rendaOuFaturamentoMensal || 0),
          total_venda: Number(localData.valorFinanciado || 0),
          total_entrada: 0,
          total_financiado: Number(localData.valorFinanciado || 0),
          parcelas_sugeridas: 12  
      },
      dados_adicionais: [{
        id: 'revendedor',
        valor: {
          documento: formState.revenda?.cnpj_cpf?.replace(/[^a-zA-Z0-9]/g,'') || '',
          nome: formState.revenda?.nome || '',
          fantasia: formState.revenda?.fantasia || ''
        }
      },
      {
        id: "projeto",
        valor: { 
          valor_servico: 0,
          valor_componentes: 0, "potencia": 0
        }
      },{
          id: "consumo",
          valor: {
            valor_mensal: 0,
            tipo: "kwh",
            distribuidor: "Não especificado"
          }
        }
      ]
    }

    financiamentoProvider.simularFinanciamento(payload).then((response)=>{
      const responseFinanceira = response?.data?.data?.filter(d => d.financeira === data?.financeira?.value);
      if (responseFinanceira && responseFinanceira.length > 0) {
        const financeira = responseFinanceira[0];
        if (financeira.mensagem && financeira.mensagem.length > 0) {
          financeira.mensagem.forEach(msg=>{
            if(regex.test(msg.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase())){
              setErrorsInSimulation(["Não existe valor pré-aprovado"]);
            }
          })
          if(!errorsInSimulation.length){
            //desconsidera a mensagem vinda da api para erros e apenas apresenta a mensagem a seguir
            setErrorsInSimulation(["Serviço indisponível"]);
          }
          return;
        } else if (financeira?.parcelas && financeira?.parcelas?.length === 0) {
          setErrorsInSimulation(["Não existe valor pré-aprovado"]);
          return;
        }
      }
      
      const result: SimulacaoFinanceira[] = response ? response.data.data.map(item => ({
        financeira: item.financeira,
        parcelas: item.parcelas ? item.parcelas.map((item): SimulacaoParcelaItem => ({
          parcela: item.parcela,
          valor: item.valor_parcela
        })) : []
      })) : []

      onChangeFormState({ 
        cpf_cnpj_proposta: localData.cpfCnpj,
        simulacao_financiamento: {
          simulacoes: result,
          cpfCnpj: localData.cpfCnpj.replace(/[^a-zA-Z0-9]/g,''),
          dataNascimentoOuFundacao: localData.dataNascimentoOuFundacao,
          financeira: localData.financeira?.value || '',
          rendaOuFaturamentoMensal: Number(localData.rendaOuFaturamentoMensal || 0),
          valorFinanciado: Number(localData.valorFinanciado || 0),
        } })
      setResult(result)
    }).catch(() => {
      setErrorsInSimulation(['Não foi possível realizar a simulação.'])
    }).finally(() => setIsLoading(false))
  }, [onChangeFormState, financeirasOpts, data, formState, isCpf])

  useEffect(()=>{
    if (errorsInSimulation.length > 0) {
      const newMsgs = errorsInSimulation;
      notifierStore.setNotifier({
        title: "Atenção!!!",
        content: newMsgs,
        timeOut: 3500,
        onClose: () => {
          notifierStore.setOpen(false);
        }
      });
      
      setErrorsInSimulation([])
      notifierStore.setOpen(true);
    }
  },[errorsInSimulation, open])

  const selectedSimulacao = useMemo(()=>{
    return result.find(item => item.financeira === data.financeira?.value)
  },[data.financeira?.value, result])

  const renderParcelas = useCallback(()=>{
    if (!selectedSimulacao || !selectedSimulacao.parcelas.length) return;

    return (
      <Box mt={2} mb={4}>
        <TableContainer>
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell align="left">
                  <strong>Quantidade de parcelas</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Valor (R$)</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedSimulacao && selectedSimulacao.parcelas.map(item => (
                <TableRow key={item.parcela}>
                  <TableCell width={"50%"} align="left" className={classes.installmentItem}>{item.parcela}x</TableCell>
                  <TableCell width={"50%"} align="center">{Formater.toString(item.valor, TypeRecords.CURRENCY)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  },[classes.installmentItem, selectedSimulacao])

  const allFieldAreFilled = useMemo(()=>{
    const requiredFields: (keyof DataProps)[] = [
      "cpfCnpj",
      "dataNascimentoOuFundacao",
      "rendaOuFaturamentoMensal",
      "financeira",
      "valorFinanciado"
    ]

    return requiredFields.every((field) => {
      return !!data[field]
    })
  },[data])

  if (!open) return null;

  return (
    <div>
      <Dialog 
        maxWidth="sm"
        open={open || false}
        onClose={onClose}
      >
        <DialogTitle>
          <Typography variant='h5' color='textPrimary'>
            Simulação de financiamento
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            Confira as opções de financiamento ofertadas pela Intelbras
          </Typography>
        </DialogTitle>

        <Box paddingBottom={2}>
          <Divider />
        </Box>

        <DialogContent>
          <InfoModal />
          <InputCPFCNPJ
            label="CPF/CNPJ"
            disabled={isLoading}
            maxLength={18}
            value={cpfCnpj({ target: { value: data.cpfCnpj } })?.target?.value || ''}
            onChangeValue={(newValue) => {
              setData(old => ({...old, cpfCnpj: newValue.value }))
            }}
          />
          {data?.cpfCnpj && !cpfOrCnpjIsValid ? (
            data?.cpfCnpj?.length === 14 ? 
            <span style={{color: 'red'}}>Não é possível dar andamento na simulação, pois o CPF informado não é válido.</span>
            : data?.cpfCnpj?.length === 18 ? 
            <span style={{color: 'red'}}>Não é possível dar andamento na simulação, pois o CNPJ informado não é válido.</span>
            : ""
          ) : (
            ""
          )}
          {cpfOrCnpjIsValid && (
            <>
              <InputDateMui
                disabled={isLoading}
                margin="dense"
                label={`* ${isCpf ? 'Data de nascimento do cliente' : 'Data de criação da empresa'}`}
                fullWidth
                value={data.dataNascimentoOuFundacao}
                onChange={(value: any) => {
                  setData(old => ({...old, dataNascimentoOuFundacao: value}))
                }}
              />

              <InputDecimal3
                showThousandSeparator
                disabled={isLoading}
                InputLabelProps={{ shrink: true }}
                label={`* ${isCpf ? 'Renda mensal do cliente' : 'Faturamento mensal da empresa'}`}
                margin="normal"
                decimal={2}
                maxLength={20}
                placeholder="R$ 0,00"
                prefix={data.rendaOuFaturamentoMensal ? <>R$&nbsp;</> : ''}
                value={data.rendaOuFaturamentoMensal ? String(data.rendaOuFaturamentoMensal || 0) : ''}
                onChange={(event: any) => {
                  setData(old => ({...old, rendaOuFaturamentoMensal: event.target.value}))
                }}
              />
            </>
          )}

          <InputComboBoxList
            disabled={isLoading}
            id={`financeiras-list`}
            key={`financeiras`}
            label={"* Financeira"}
            disableClearable
            textField="text"
            valueField="value"
            options={financeirasOpts}
            value={data.financeira}
            autoComplete
            renderOptions={(option: any) => {
              return `${option.text}`
            }}
            renderOptionsLabel={(option: any) => {
              return `${option.text}`
            }}
            onChange={(value: any) => {
              setData(old => ({...old, financeira: value}))
            }}
          />

          <InputDecimal3
            showThousandSeparator
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
            label="* Qual valor será financiado?"
            margin="normal"
            decimal={2}
            maxLength={20}
            placeholder="R$ 0,00"
            prefix={data.valorFinanciado ? <>R$&nbsp;</> : ''}
            value={data.valorFinanciado ? String(data.valorFinanciado || 0) : ''}
            onChange={(event: any) => {
              setData(old => ({...old, valorFinanciado: event.target.value}))
            }}
          />

          {isLoading ? (
            <Box my={4} width='100%' display='flex' justifyContent={'center'} alignItems='center'>
              <span>Simulando ...</span>
            </Box>
          ) : (
            renderParcelas()
          )}

          <Box display='flex' width='100%' justifyContent={"center"} mt={2}>
            <Button onClick={() => simular()} disabled={!allFieldAreFilled || isLoading} variant="contained" color="primary">Simular</Button>
          </Box>

          <ul className={classes.list}>
            <li>
              <Typography color="textSecondary" variant='body2'>
                O prazo de carência do contrato é de 90 dias a contar a partir da data de assinatura do cliente.
              </Typography>
            </li>
            <li>
              <Typography color="textSecondary" variant='body2'>
                As condições apresentadas representam apenas uma simulação e não valem como proposta de financiamento, e a confirmação dos valores depende de análise cadastral e de crédito da instituição financeira.
              </Typography>
            </li>
          </ul>
        
        </DialogContent>
        <DialogActions>
          <FlatButton
            onClick={onClose}
            color="primary"
            variant="contained"
            text={"Fechar"}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export { ModalSimulacaoFinanciamento };
